import * as React from 'react';
import * as _ from 'lodash';
import * as queryString from 'query-string';
import * as moment from 'moment';
import { connect, Dispatch } from 'react-redux';
import { IApplicationState, SwapActionCreators, AppActionCreators, IAppState, IMoveTrackerListResponse, IGpsBatchListResponse } from '@nkr/store/index';
import { FormControl, Glyphicon } from 'react-bootstrap';
import { RouteComponentProps } from 'react-router';
import { FormControlWrapper } from '../swap-management/swaps/styles';
import { DisplayType } from '@nkr/enums/index';
import SwapList from './swap-list';
import { IMarker, IPolyline, IInProcessSwap, refreshIntervals, filterTypes, IDisplaySetting } from './models';
import { IMoveTracker } from '@nkr/models/index';
import NkrMap from './nkr-map';
import Toolbar from './toolbar';

interface IOwnProps extends RouteComponentProps<{}> {
    isOps: boolean
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    changeDisplay: (display: number) => void,
    getSwapsInProcess: (isOps: boolean) => Promise<IMoveTrackerListResponse>,
    getSwap: (chainId: number, clusterNumber: number, positionNumber: number, isOps: boolean) => Promise<IMoveTrackerListResponse>,
    getGpsDataBatch: (list: { imei: string, startDate: string }[], isOps: boolean) => Promise<IGpsBatchListResponse>
}

type TProps = IOwnProps & IStateProps & IDispatchProps;

interface IState {
    list: IDisplaySetting[],
    display: IMarker[],
    flightLines: IPolyline[],
    filter: number,
    zoomValue: number,
    zoom: number,
    single: boolean,
    swapNumber: string,
    swapSelection: boolean,
    autoRefresh: number,
    refresh: number,
    refreshEndDate: Date,
    refreshText: string,
    showToolbar: boolean,
    expandSwapList: boolean,
    center: {
        lat: number,
        lng: number
    },
    loading: boolean
}

class BeaconOverlayMap extends React.Component<TProps, IState> {
    readonly state: IState = {
        list: [],
        display: [],
        flightLines: [],
        filter: 0,
        zoomValue: 5,
        zoom: __ENVIRONMENT__ == 'dev2' ? 11 : 5,
        single: false,
        swapNumber: '',
        swapSelection: false,
        autoRefresh: 0,
        refresh: 0,
        refreshEndDate: null,
        refreshText: '',
        showToolbar: true,
        expandSwapList: true,
        center: __ENVIRONMENT__ == 'dev2' ? { lat: 33.427934724890555, lng: -111.95844439697264 } : { lat: 39.8283, lng: -98.5795 },
        loading: false
    };

    colors: string[] = [
        '#33FF99', // 1
        '#33FFFF', // 2
        '#3399FF', // 3
        '#B800B8', // 4
        '#FF3333', // 5
        '#FF9933', // 6
        '#FFFF4D', // 7
        '#FFBF80', // 8
        '#FF99E0', // 9
        '#B82E2E', // 10
        //'#B8ABFE', // 11
        //'#C7BEFF', // 12
        //'#D7D1FF'  // 13
    ];

    timeoutRef: any;

    componentDidMount = () => {
        let parsed = queryString.parse(this.props.location.search);

        if (parsed.s == null) {
            this.setState({
                single: false
            }, () => {
                this.props.showLoading('Getting swaps, one moment please...').then(() => {
                    this.getCurrentSwaps();
                });
            });
        } else {
            this.setState({
                single: true,
                swapNumber: parsed.s
            }, () => {
                this.props.showLoading('Getting the swap, one moment please...').then(() => {
                    this.getSingleSwap();
                });
            });
        }
    }

    getCurrentSwaps = () => {
        this.props.getSwapsInProcess(this.props.isOps).then(async response => {
            if (response.data.success) {
                let updatedSwaps = await this.getGpsData(response.data.results);

                let list = updatedSwaps.filter(swap => swap != undefined).map((swap, index) => {
                    return { ...swap, color: this.colors[index], show: true };
                });

                let refreshEndDate = moment().add(this.state.autoRefresh, 'second').toDate();

                this.setState({
                    list,
                    single: false,
                    loading: false,
                    refreshEndDate: this.state.loading ? refreshEndDate : null,
                    refreshText: this.state.loading ? this.getCountdown(this.calculateTimeLeft(refreshEndDate)) : ''
                }, () => {
                    if (this.state.autoRefresh > 0) {
                        this.timeoutRef = setTimeout(this.timeout.bind(this), 1000);
                    }

                    this.applySwaps();
                    this.props.hideLoading();
                });
            } else {
                let refreshEndDate = moment().add(this.state.autoRefresh, 'second').toDate();

                this.setState({
                    single: false,
                    loading: false,
                    refreshEndDate: this.state.loading ? refreshEndDate : null,
                    refreshText: this.state.loading ? this.getCountdown(this.calculateTimeLeft(refreshEndDate)) : ''
                }, () => {
                    if (this.state.autoRefresh > 0) {
                        this.timeoutRef = setTimeout(this.timeout.bind(this), 1000);
                    }

                    this.props.hideLoading();
                });
            }
        });
    }

    getSingleSwap = () => {
        let parts = this.state.swapNumber.split('/');

        if (parts.length == 3) {
            let chainId = parseInt(parts[0]);
            let clusterNumber = parseInt(parts[1]);
            let positionNumber = parseInt(parts[2]);

            this.props.getSwap(chainId, clusterNumber, positionNumber, this.props.isOps).then(async response => {
                let updatedSwaps = await this.getGpsData(response.data.results);

                let list = updatedSwaps.map((swap, index) => {
                    return { ...swap, color: this.colors[index], show: true };
                });

                let refreshEndDate = moment().add(this.state.autoRefresh, 'second').toDate();

                this.setState({
                    list,
                    loading: false,
                    refreshEndDate: this.state.loading ? moment().add(this.state.autoRefresh, 'second').toDate() : null,
                    refreshText: this.state.loading ? this.getCountdown(this.calculateTimeLeft(refreshEndDate)) : ''
                }, () => {
                    if (this.state.autoRefresh > 0)
                        this.timeoutRef = setTimeout(this.timeout.bind(this), 1000);

                    this.applySwaps();
                    this.props.hideLoading();
                });
            });
        }
    }

    getGpsData = async (swaps: IMoveTracker[]) => {
        let list: IInProcessSwap[] = [];

        let swapsWithGps = swaps.filter(x => x.gpsAutomation != null);
        let gpsRequestData = swapsWithGps.map(x => ({ imei: x.gpsAutomation.imei, startDate: x.kidneyPickup, swap: `${x.chainId}/${x.clusterNumber}/${x.positionNumber}` }));

        try {
            let response = await this.props.getGpsDataBatch(gpsRequestData, this.props.isOps);

            list = swapsWithGps.map(swap => {
                let result = response.data.results.find(x => x.success && x.imei == swap.gpsAutomation.imei);

                return { ...swap, gpsResults: result != null ? result.results : [] };
            });
        } catch (error) {
            console.log(error);
        }

        return list;
    }

    getPickupDate = (swap: IInProcessSwap) => {
        if (swap.pickup.actualDateTimeLocal != '') {
            return moment(swap.pickup.actualDateTimeLocal, 'MM/DD HH:mm').toDate();
        }

        return new Date(swap.kidneyPickupLocal);
    }

    applySwaps = () => {
        let display: IMarker[] = [];

        this.state.list.forEach(swap => {
            if ((swap.donorCenterId == swap.recipientCenterId && swap.rdnCenterId == 0) == false) {
                let inProcess = this.getPickupDate(swap) <= new Date() && this.state.filter == 1;
                let scheduled = new Date(swap.kidneyPickupLocal) > new Date() && this.state.filter == 2;
                let allSwaps = this.state.filter == 0;

                if ((inProcess || scheduled || allSwaps) && swap.show) {
                    let gpsCount = swap.gpsResults.length - 1;

                    swap.gpsResults.forEach((gps, index) => {
                        display.push({
                            lat: gps.lat,
                            lng: gps.lng,
                            text: `${swap.chainId}/${swap.clusterNumber}/${swap.positionNumber}`,
                            color: swap.color,
                            data: swap,
                            isDonorCenter: false,
                            isWaypoint: true,
                            isLast: index == gpsCount,
                            pickupDateTime: gps.tsDt,
                            arrivalDateTime: '',
                            batteryPercentage: gps.bp
                        });
                    });

                    display.push({
                        lat: swap.rdnCenterId == 0 ? swap.donorCenterLatitude : swap.rdnCenterLatitude,
                        lng: swap.rdnCenterId == 0 ? swap.donorCenterLongitude : swap.rdnCenterLongitude,
                        text: `${swap.chainId}/${swap.clusterNumber}/${swap.positionNumber}`,
                        color: swap.color,
                        data: swap,
                        isDonorCenter: true,
                        isWaypoint: false,
                        isLast: false,
                        pickupDateTime: swap.kidneyPickup,
                        arrivalDateTime: swap.kidneyArrival,
                        batteryPercentage: 0
                    });

                    display.push({
                        lat: swap.recipientCenterLatitude,
                        lng: swap.recipientCenterLongitude,
                        text: `${swap.chainId}/${swap.clusterNumber}/${swap.positionNumber}`,
                        color: swap.color,
                        data: swap,
                        isDonorCenter: false,
                        isWaypoint: false,
                        isLast: false,
                        pickupDateTime: swap.kidneyPickup,
                        arrivalDateTime: swap.kidneyArrival,
                        batteryPercentage: 0
                    });
                }
            }
        });

        this.setState({
            display
        }, () => {
            this.addFlightLines();
        });
    }

    addFlightLines = () => {
        let flightLines: IPolyline[] = [];

        this.state.list.forEach(swap => {
            if ((swap.donorCenterId == swap.recipientCenterId && swap.rdnCenterId == 0) == false) {
                let inProcess = this.getPickupDate(swap) <= new Date() && this.state.filter == 1;
                let scheduled = new Date(swap.kidneyPickupLocal) > new Date() && this.state.filter == 2;
                let allSwaps = this.state.filter == 0;

                if ((inProcess || scheduled || allSwaps) && swap.show) {
                    if (swap.gpsResults.length > 0) {
                        let path = [];

                        for (let index = 0; index < swap.gpsResults.length; index++) {
                            let s = swap.gpsResults[index]
                            path.push({ 'lat': s.lat, 'lng': s.lng });
                        }

                        path.push({ 'lat': swap.recipientCenterLatitude, 'lng': swap.recipientCenterLongitude });

                        flightLines.push({
                            path,
                            strokeColor: swap.color,
                            strokeOpacity: 1,
                            strokeWeight: 2,
                            swap
                        });
                    } else {
                        let dcLat = swap.rdnCenterId == 0 ? swap.donorCenterLatitude : swap.rdnCenterLatitude;
                        let dcLng = swap.rdnCenterId == 0 ? swap.donorCenterLongitude : swap.rdnCenterLongitude;

                        flightLines.push({
                            path: [
                                { 'lat': dcLat, 'lng': dcLng },
                                { 'lat': swap.recipientCenterLatitude, 'lng': swap.recipientCenterLongitude }
                            ],
                            strokeColor: swap.color,
                            strokeOpacity: 1,
                            strokeWeight: 2,
                            swap
                        });
                    }
                }
            }
        });

        this.setState({
            flightLines
        });
    }

    removeFlightLines = () => {
        this.setState({
            flightLines: []
        });
    }

    getFilteredSwaps = () => {
        let list = this.state.list.filter(swap => {
            if (swap.donorCenterId == swap.recipientCenterId && swap.rdnCenterId == 0) {
                return false;
            }

            var inProcess = (new Date(swap.kidneyPickupLocal) <= new Date() || swap.activeLogisticsPlan == '2') && this.state.filter == 1;
            var scheduled = new Date(swap.kidneyPickupLocal) > new Date() && this.state.filter == 2;
            var allSwaps = this.state.filter == 0;

            if (inProcess || scheduled || allSwaps) {
                return true;
            }

            return false;
        });

        return list;
    }

    calculateTimeLeft = (refreshEndDate: Date) => {
        const difference = +refreshEndDate - +new Date();

        let timeLeft = {};

        if (difference > 0) {
            timeLeft = {
                days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((difference / 1000 / 60) % 60),
                seconds: Math.floor((difference / 1000) % 60)
            };
        }

        return timeLeft;
    };

    refresh = () => {
        this.setState({
            loading: true,
            refreshEndDate: null
        }, () => {
            if (this.state.single == false) {
                this.getCurrentSwaps();
            } else {
                this.getSingleSwap();
            }
        });
    }

    timeout = () => {
        clearTimeout(this.timeoutRef);

        let timeLeft = this.calculateTimeLeft(this.state.refreshEndDate);

        let keyCount = Object.keys(timeLeft).length;

        if (keyCount > 0) {
            let countdown = this.getCountdown(timeLeft);

            this.setState({
                refreshText: countdown
            }, () => {
                this.timeoutRef = setTimeout(this.timeout.bind(this), 1000);
            });
        } else {
            this.refresh();
        }
    }

    getCountdown = (timeLeft: {}) => {
        let keyCount = Object.keys(timeLeft).length;
        let minutes = keyCount > 0 ? timeLeft['minutes'].toString() : '';
        let seconds = keyCount > 0 ? (timeLeft['seconds'].toString().length == 1 ? '0' + timeLeft['seconds'].toString() : timeLeft['seconds'].toString()) : '';
        let current = `0${minutes}:${seconds}`;

        return current;
    }

    handleShowToolbar = () => {
        this.setState({
            showToolbar: !this.state.showToolbar
        });
    }

    handleChangeDisplay = (display: DisplayType) => {
        this.props.changeDisplay(display);
    }

    handleAutoRefreshChange = (autoRefresh: number) => {
        let refreshEndDate = autoRefresh > 0 ? moment().add(autoRefresh, 'second').toDate() : null;
        let refreshText = autoRefresh > 0 ? this.getCountdown(this.calculateTimeLeft(refreshEndDate)) : '';

        clearTimeout(this.timeoutRef);

        this.setState({
            autoRefresh,
            refreshEndDate,
            refreshText
        }, () => {
            if (autoRefresh > 0)
                this.timeoutRef = setTimeout(this.timeout.bind(this), 1000);
        });
    }

    handleFilterChange = (filter: number) => {
        this.setState({
            filter
        }, () => {
            this.removeFlightLines();
            this.applySwaps();
        });
    }

    handleZoomChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.setState({
            zoom: parseInt(e.currentTarget.value)
        });
    }

    handleExpand = () => {
        this.setState({
            expandSwapList: !this.state.expandSwapList,
            showToolbar: !this.state.showToolbar
        });
    }

    handleShowSwap = (chainId: number, clusterNumber: number, positionNumber: number) => {
        let list = _.cloneDeep(this.state.list).map(swap => {
            if (swap.chainId == chainId && swap.clusterNumber == clusterNumber && swap.positionNumber == positionNumber) {
                swap.show = !swap.show;
            }

            return swap;
        })

        this.setState({
            list
        }, () => {
            this.applySwaps();
        });
    }

    handleShowAllSwaps = (show: boolean) => {
        let list = this.state.list.map(swap => {
            swap.show = show;
            return swap;
        });

        this.setState({
            list
        }, () => {
            this.applySwaps();
        });
    }

    render = () => {
        const swapList = this.getFilteredSwaps();
        const title = filterTypes[this.state.filter];
        const marginTop = this.props.app.display == DisplayType.NoMenuNoTabs ? 10 : 0;

        return (
            <>
                <div className='container-fluid'>
                    {this.state.single == false ?
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 30, marginBottom: 20, marginTop }}>
                            <div style={{ flexBasis: '36%', fontSize: 28, fontWeight: 500 }}>
                                BEACON OVERLAY MAP - {title}
                            </div>

                            <div style={{ flexBasis: '27%' }}>
                                {this.state.loading && this.state.autoRefresh == 0 && (
                                    <div className='alert alert-info' style={{ marginBottom: 0, padding: 7.5, textAlign: 'center' }}>Getting swaps, one moment please...</div>
                                )}

                                {this.state.autoRefresh > 0 && (
                                    <div className='alert alert-info' style={{ marginBottom: 0, padding: 7.5, textAlign: 'center' }}>{this.state.refreshEndDate != null ? this.state.refreshText : 'Getting swaps, one moment please...'}</div>
                                )}
                            </div>

                            <div>
                                <Toolbar
                                    display={this.props.app.display}
                                    autoRefresh={this.state.autoRefresh}
                                    filter={this.state.filter}
                                    expandSwapList={this.state.expandSwapList}
                                    onDisplayChange={this.handleChangeDisplay}
                                    onAutoRefreshChange={this.handleAutoRefreshChange}
                                    onRefresh={this.refresh}
                                    onFilterChange={this.handleFilterChange}
                                    onExpand={this.handleExpand}
                                />
                            </div>
                        </div>
                        :
                        <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', height: 30, marginBottom: 20, marginTop: 0 }}>
                            <div style={{ flexBasis: '33%', fontSize: 28, fontWeight: 500 }}>
                                BEACON OVERLAY MAP - {title}
                            </div>

                            <div style={{ flexBasis: '33%' }}>
                                {this.state.loading && this.state.autoRefresh == 0 && (
                                    <div className='alert alert-info' style={{ marginBottom: 0, padding: 7.5, textAlign: 'center' }}>Getting swap, one moment please...</div>
                                )}

                                {this.state.autoRefresh > 0 && (
                                    <div className='alert alert-info' style={{ marginBottom: 0, padding: 7.5, textAlign: 'center' }}>{this.state.refreshEndDate != null ? this.state.refreshText : 'Getting swap, one moment please...'}</div>
                                )}
                            </div>

                            <div style={{ flexBasis: '33%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{ marginRight: 5 }}>Auto refresh:</div>
                                    <div style={{ marginRight: 5 }}>
                                        <FormControlWrapper
                                            value={this.state.autoRefresh}
                                            componentClass='select'
                                            style={{ fontSize: 12 }}
                                            onChange={this.handleAutoRefreshChange}
                                        >
                                            {refreshIntervals.map((interval, index) =>
                                                <option key={index} value={interval.value}>{interval.label}</option>
                                            )}
                                        </FormControlWrapper>
                                    </div>
                                    <div>
                                        <Glyphicon glyph='refresh' title='Refresh now' onClick={this.refresh} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    }

                    {this.state.single == false && this.state.expandSwapList && (
                        <SwapList
                            swapList={swapList}
                            // displaySettings={this.state.displaySettings}
                            onShowSwap={this.handleShowSwap}
                            onShowAllSwaps={this.handleShowAllSwaps}
                        />
                    )}

                    <NkrMap
                        swapList={swapList}
                        single={this.state.single}
                        display={this.state.display}
                        flightLines={this.state.flightLines}
                        center={this.state.center}
                        zoom={this.state.zoom}
                    />
                </div>
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        changeDisplay: (display: number) => {
            dispatch(AppActionCreators.changeDisplay(display));
        },
        getSwapsInProcess: (isOps) => {
            return dispatch(SwapActionCreators.getSwapsInProcess(isOps));
        },
        getSwap: (chainId, clusterNumber, positionNumber, isOps) => {
            return dispatch(SwapActionCreators.getSwap(chainId, clusterNumber, positionNumber, isOps));
        },
        getGpsDataBatch: (list, isOps) => {
            return dispatch(SwapActionCreators.getGpsDataBatch(list, isOps));
        }
    };
}

export default connect<{}, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(BeaconOverlayMap);

